import React, { Component, type ReactNode } from 'react';

/**
 * A classic React Error Boundary specifically designed to wrap
 * dynamic Editor blocks. If an individual block crashes (e.g. from
 * missing Magento API data or bad syntax), this prevents the entire
 * page tree from unmounting into an empty white screen.
 */
interface Props {
  children: ReactNode;
  blockType?: string;
  blockId?: string;
}

interface State {
  hasError: boolean;
  errorMsg: string;
}

export class BlockErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, errorMsg: '' };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, errorMsg: error.message };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const bType = this.props.blockType || 'unknown';
    const bId = this.props.blockId || 'unknown';
    console.error(`[ErrorBoundary] Block "${bType}" (${bId}) Crashed:`, error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      // In production, fail silently or show a highly subtle skeleton so the UX isn't ruined
      // In dev, showing the blockType is helpful.
      return (
        <div style={{ display: 'none' }} data-error-block={this.props.blockType || 'unknown'} />
      );
    }

    return this.props.children;
  }
}
